import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
  Icon,
  IconSize,
  Text,
  Tooltip,
} from '@equip.health/ui';

import { AssessmentDisplayStatus } from './AssessmentDisplayStatus';
import { AssessmentFrequency } from './AssessmentFrequency';

const idPrefix = 'assessment-card-header-tooltip';

export interface AssessmentCardHeaderProps {
  descriptionText: string;
  assessmentName: string;
  mostRecentAssessmentStatus?: AssessmentDisplayStatus; // undefined if the card has no data
  frequency: AssessmentFrequency;
  mostRecentAssessmentResponseId?: string; // undefined if the card has no data
  isAssessmentActive: boolean;
  isHistoryEnabled: boolean;
  // When true, suppresses the "Create new" action (both the kebab item and the
  // inline primary CTA). Resume, View, and Delete remain available. Used for
  // surveys that shouldn't spawn new instances but still need history access.
  hideCreateActions?: boolean;
  onCreateNewClick: (source: 'title' | 'kebab') => void;
  onResumeClick: (assessmentResponseId: string) => void;
  onViewAssessmentClick: (assessmentResponseId: string) => void;
  onDeleteClick: (assessmentResponseId: string) => void;
  onViewHistoryClick: () => void;
}

const AssessmentCardHeader = ({
  assessmentName,
  descriptionText,
  frequency,
  mostRecentAssessmentResponseId,
  mostRecentAssessmentStatus,
  isAssessmentActive,
  isHistoryEnabled,
  hideCreateActions = false,
  onCreateNewClick,
  onResumeClick,
  onViewAssessmentClick,
  onDeleteClick,
  onViewHistoryClick,
}: AssessmentCardHeaderProps) => {
  const kebabMenuOptions: FireflyKebabMenuItem[] = [];
  if (!hideCreateActions && isAssessmentActive) {
    kebabMenuOptions.push({
      label: 'Create new',
      leftIcon: FAIconName.plusSolid,
      onClick: () => onCreateNewClick('kebab'),
    });
  }
  if (mostRecentAssessmentResponseId) {
    kebabMenuOptions.push({
      label: 'View',
      leftIcon: FAIconName.externalLinkRegular,
      onClick: () => onViewAssessmentClick(mostRecentAssessmentResponseId),
    });
  }
  if (
    mostRecentAssessmentResponseId &&
    mostRecentAssessmentStatus &&
    [AssessmentDisplayStatus.NEW, AssessmentDisplayStatus.IN_PROGRESS].includes(
      mostRecentAssessmentStatus,
    )
  ) {
    kebabMenuOptions.push({
      leftIcon: FAIconName.trashRegular,
      leftIconColor: 'critical.base',
      label: 'Delete',
      onClick: () => onDeleteClick(mostRecentAssessmentResponseId),
    });
  }

  return (
    <ChakraFlex alignItems="center" justifyContent="space-between" width="100%">
      <ChakraFlex alignItems="center" gap="24px">
        <ChakraFlex alignItems="center" gap="4px">
          <Text color="neutral.primary" variant="h5">
            {assessmentName}
          </Text>
          <Tooltip
            id={`${idPrefix}-${assessmentName}`}
            placement="bottom-end"
            tooltipText={descriptionText}
          >
            <Icon
              aria-hidden={false}
              aria-label="Hover for assessment description"
              color="neutral.secondary"
              name={FAIconName.infoCircleRegular}
              role="img"
              size={IconSize.medium}
            />
          </Tooltip>
        </ChakraFlex>
        <Text color="neutral.secondary" variant="caption">
          {frequency}
        </Text>
      </ChakraFlex>
      <ChakraFlex alignItems="center" gap="12px">
        {isHistoryEnabled && (
          <FireflyButton
            id={`${idPrefix}-history-btn-${assessmentName}`}
            leftIcon={FAIconName.rectangleVerticalHistoryRegular}
            onClick={() => onViewHistoryClick()}
            size="small"
            variant="secondary"
          >
            View history
          </FireflyButton>
        )}
        {mostRecentAssessmentResponseId &&
          mostRecentAssessmentStatus &&
          [
            AssessmentDisplayStatus.NEW,
            AssessmentDisplayStatus.IN_PROGRESS,
          ].includes(mostRecentAssessmentStatus) && (
            <FireflyButton
              id={`${idPrefix}-action-btn-${assessmentName}`}
              leftIcon={FAIconName.penLine}
              onClick={() => onResumeClick(mostRecentAssessmentResponseId)}
              size="small"
              variant="primary"
            >
              Resume
            </FireflyButton>
          )}
        {!hideCreateActions &&
          mostRecentAssessmentResponseId &&
          isAssessmentActive &&
          mostRecentAssessmentStatus &&
          [
            AssessmentDisplayStatus.COMPLETE,
            AssessmentDisplayStatus.INCOMPLETE,
          ].includes(mostRecentAssessmentStatus) && (
            <FireflyButton
              id={`${idPrefix}-action-btn-${assessmentName}`}
              leftIcon={FAIconName.plusSolid}
              onClick={() => onCreateNewClick('title')}
              size="small"
              variant="primary"
            >
              Create New
            </FireflyButton>
          )}

        {kebabMenuOptions.length > 0 && (
          <FireflyKebabMenu
            aria-label="Assessment options menu"
            options={kebabMenuOptions}
          />
        )}
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default AssessmentCardHeader;
